import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowRight, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminImport() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [token, setToken] = useState(null);
  const [mode, setMode] = useState("upsert");
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);

  const reset = () => {
    setFile(null); setPreview(null); setToken(null); setError("");
    setCommitResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (f) => {
    if (!f) return;
    setError("");
    setPreview(null); setToken(null); setCommitResult(null);
    setFile(f);
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/import/preview`, fd, { withCredentials: true });
      setPreview(r.data);
      setToken(r.data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось обработать файл");
    } finally { setLoading(false); }
  };

  const doCommit = async () => {
    if (!token) return;
    setCommitting(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/import/commit`, { token, mode }, { withCredentials: true });
      setCommitResult(r.data);
      setPreview(null); setToken(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка импорта");
    } finally { setCommitting(false); }
  };

  return (
    <div className="light-section" data-testid="admin-import-page" style={{ maxWidth: "900px" }}>
      <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#6b7280", marginBottom: "20px", lineHeight: 1.6 }}>
        Загрузите Excel-файл, выгруженный из x2pos (adishop template). Система автоматически распознает
        товары, привяжет к категориям и покажет предпросмотр перед добавлением.
      </div>

      {/* File picker */}
      {!preview && !commitResult && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "28px" }}>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.xlsm" style={{ display: "none" }}
            data-testid="import-file-input"
            onChange={(e) => onFile(e.target.files?.[0])} />

          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
            data-testid="import-dropzone"
            style={{
              border: "2px dashed #C5A059", borderRadius: "10px", padding: "36px 16px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
              cursor: "pointer", background: file ? "rgba(197,160,89,0.05)" : "#FDFBF7",
              transition: "background 0.2s",
            }}>
            {file ? (
              <>
                <FileSpreadsheet size={36} style={{ color: "#0A8043" }} />
                <div style={{ fontFamily: "Manrope", fontSize: "14px", color: "#0A0A0A", fontWeight: 600 }}>{file.name}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280" }}>{(file.size / 1024).toFixed(0)} КБ — нажмите чтобы заменить</div>
              </>
            ) : (
              <>
                <Upload size={32} style={{ color: "#C5A059" }} />
                <div style={{ fontFamily: "Manrope", fontSize: "14px", color: "#0A0A0A", fontWeight: 600 }}>Выберите Excel-файл из x2pos</div>
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280" }}>Поддерживаются .xlsx, .xls</div>
              </>
            )}
          </div>

          {error && <div style={{ marginTop: "14px", padding: "10px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: "6px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", gap: "10px" }}>
            {file && <button onClick={reset} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px", color: "#6b7280" }}>Отмена</button>}
            <button data-testid="import-preview-btn" onClick={doPreview} disabled={!file || loading}
              style={{ background: file ? "linear-gradient(135deg,#C5A059,#D4B878)" : "#e5e7eb", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: file ? "pointer" : "default", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
              {loading ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <ArrowRight size={14} />}
              {loading ? "Обработка..." : "Предпросмотр"}
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div data-testid="import-preview" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", margin: 0 }}>Предпросмотр</h3>
            <button onClick={reset} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={18} /></button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "10px", marginBottom: "20px" }}>
            {[
              { label: "Всего строк", value: preview.summary.total, color: "#0A0A0A" },
              { label: "Будет создано", value: preview.summary.to_create, color: "#0A8043" },
              { label: "Будет обновлено", value: preview.summary.to_update, color: "#C5A059" },
            ].map(s => (
              <div key={s.label} style={{ background: "#FDFBF7", borderRadius: "8px", padding: "14px", textAlign: "center", border: "1px solid #f3f4f6" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", fontWeight: 600, color: s.color }}>{s.value}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#9ca3af", marginTop: "2px" }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px", fontWeight: 700 }}>По категориям</div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
            {Object.entries(preview.summary.by_category).map(([c, n]) => (
              <span key={c} style={{ background: "#fef3c7", color: "#92400e", fontFamily: "Manrope", fontSize: "11px", padding: "4px 10px", borderRadius: "12px", fontWeight: 600 }}>{c}: {n}</span>
            ))}
          </div>

          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px", fontWeight: 700 }}>Первые товары (до 50)</div>
          <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #f3f4f6", borderRadius: "6px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: "12px" }}>
              <thead style={{ background: "#FDFBF7", position: "sticky", top: 0 }}>
                <tr>
                  <th style={th}>Артикул</th>
                  <th style={th}>Название</th>
                  <th style={th}>Категория</th>
                  <th style={{ ...th, textAlign: "right" }}>Цена</th>
                  <th style={th}>Действие</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((row, idx) => (
                  <tr key={idx} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={td}>{row.sku || <span style={{ color: "#dc2626" }}>—</span>}</td>
                    <td style={td}>{row.name}</td>
                    <td style={td}><span style={{ background: "#f3f4f6", padding: "2px 6px", borderRadius: "4px", fontSize: "11px" }}>{row.category}</span></td>
                    <td style={{ ...td, textAlign: "right" }}>{row.price ? row.price.toLocaleString("ru-RU") + " ₸" : "—"}</td>
                    <td style={td}>
                      {row.match === "update" ? (
                        <span style={{ color: "#C5A059", fontWeight: 600 }}>Обновить</span>
                      ) : (
                        <span style={{ color: "#0A8043", fontWeight: 600 }}>Создать</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "20px", padding: "12px 14px", background: "#FDFBF7", borderRadius: "6px", border: "1px solid #f3f4f6" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Режим</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {[
                { v: "upsert", l: "Создать и обновить (по умолчанию)" },
                { v: "create_only", l: "Только новые товары" },
                { v: "update_only", l: "Только обновить существующие" },
              ].map(o => (
                <label key={o.v} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", background: mode === o.v ? "rgba(197,160,89,0.15)" : "white", border: `1px solid ${mode === o.v ? "#C5A059" : "#e5e7eb"}`, borderRadius: "6px", cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>
                  <input type="radio" name="mode" value={o.v} checked={mode === o.v} onChange={() => setMode(o.v)} style={{ accentColor: "#C5A059" }} />
                  {o.l}
                </label>
              ))}
            </div>
          </div>

          {error && <div style={{ marginTop: "12px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", gap: "10px" }}>
            <button onClick={reset} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px", color: "#6b7280" }}>Отмена</button>
            <button data-testid="import-commit-btn" onClick={doCommit} disabled={committing}
              style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
              {committing ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <CheckCircle size={14} />}
              {committing ? "Импортируется..." : "Импортировать"}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {commitResult && (
        <div data-testid="import-result" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
            <CheckCircle size={24} style={{ color: "#0A8043" }} />
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", margin: 0 }}>Импорт завершён</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "10px", marginBottom: "16px" }}>
            {[
              { l: "Создано", v: commitResult.created, c: "#0A8043" },
              { l: "Обновлено", v: commitResult.updated, c: "#C5A059" },
              { l: "Пропущено", v: commitResult.skipped, c: "#6b7280" },
              { l: "Ошибки", v: commitResult.errors, c: "#dc2626" },
            ].map(s => (
              <div key={s.l} style={{ background: "#FDFBF7", borderRadius: "8px", padding: "12px", textAlign: "center", border: "1px solid #f3f4f6" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", fontWeight: 600, color: s.c }}>{s.v}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "2px" }}>{s.l}</div>
              </div>
            ))}
          </div>
          {commitResult.errors > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px", marginBottom: "12px" }}>
              <AlertCircle size={14} /> Часть строк не импортирована. Проверьте логи бэкенда.
            </div>
          )}
          <button onClick={reset}
            style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Импортировать ещё один файл
          </button>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>

      {/* Bulk photo upload via ZIP */}
      <BulkPhotoUploader />
    </div>
  );
}

function BulkPhotoUploader() {
  const fileRef = React.useRef(null);
  const [zipFile, setZipFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [replace, setReplace] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState("");

  const onPick = (f) => { if (f) { setZipFile(f); setResult(null); setError(""); } };

  const upload = async () => {
    if (!zipFile) return;
    setUploading(true); setError(""); setResult(null); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", zipFile);
      const r = await axios.post(`${API}/admin/photos/bulk?replace=${replace}`, fd, {
        withCredentials: true,
        timeout: 30 * 60 * 1000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setResult(r.data);
      setZipFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка загрузки");
    } finally { setUploading(false); }
  };

  return (
    <div className="light-section" style={{ marginTop: "32px", background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
        <FileSpreadsheet size={20} style={{ color: "#C5A059" }} />
        <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", margin: 0, fontWeight: 500 }}>Массовая загрузка фотографий</h3>
      </div>
      <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", lineHeight: 1.6, marginBottom: "16px" }}>
        Загрузите <b>ZIP-архив</b> с фотографиями товаров. Имя каждого файла должно быть равно <b>штрихкоду</b> или артикулу товара
        (например <code>2110086000004.jpg</code>). Система автоматически прикрепит фото к нужным товарам.
        Поддерживаются jpg, jpeg, png, webp, gif.
      </div>

      <input ref={fileRef} type="file" accept=".zip" style={{ display: "none" }}
        data-testid="bulk-zip-input"
        onChange={(e) => onPick(e.target.files?.[0])} />

      <div onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
        data-testid="bulk-zip-dropzone"
        style={{
          border: "2px dashed #C5A059", borderRadius: "10px", padding: "26px 16px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: "8px",
          cursor: "pointer", background: zipFile ? "rgba(197,160,89,0.05)" : "#FDFBF7",
        }}>
        <Upload size={26} style={{ color: "#C5A059" }} />
        {zipFile ? (
          <>
            <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A", fontWeight: 600 }}>{zipFile.name}</div>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280" }}>{(zipFile.size / 1024 / 1024).toFixed(1)} МБ</div>
          </>
        ) : (
          <>
            <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "#0A0A0A", fontWeight: 600 }}>Выберите ZIP с фото</div>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280" }}>До 500 МБ. Имена файлов = штрихкоды</div>
          </>
        )}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", fontFamily: "Manrope", fontSize: "12px", color: "#6b7280", cursor: "pointer" }}>
        <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} style={{ accentColor: "#C5A059" }} />
        Перезаписать существующие изображения (по умолчанию — добавить к имеющимся)
      </label>

      {uploading && (
        <div style={{ marginTop: "12px" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>Загрузка {progress}%...</div>
          <div style={{ height: "6px", background: "#f3f4f6", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: "linear-gradient(90deg,#C5A059,#D4B878)", transition: "width 0.3s" }} />
          </div>
        </div>
      )}

      {error && <div style={{ marginTop: "12px", color: "#dc2626", fontFamily: "Manrope", fontSize: "12px" }}>{error}</div>}

      {result && (
        <div data-testid="bulk-zip-result" style={{ marginTop: "16px", padding: "16px", background: "#FDFBF7", border: "1px solid #f3f4f6", borderRadius: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", color: "#0A8043" }}>
            <CheckCircle size={18} /><span style={{ fontFamily: "Manrope", fontWeight: 700 }}>Готово</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "8px" }}>
            {[
              { l: "Файлов", v: result.total_files, c: "#0A0A0A" },
              { l: "Привязано", v: result.matched, c: "#0A8043" },
              { l: "Товаров обновлено", v: result.updated_products, c: "#C5A059" },
              { l: "Не найдено", v: result.skipped_no_match, c: "#dc2626" },
            ].map(s => (
              <div key={s.l} style={{ background: "white", borderRadius: "6px", padding: "10px", textAlign: "center", border: "1px solid #f3f4f6" }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 600, color: s.c }}>{s.v}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "2px" }}>{s.l}</div>
              </div>
            ))}
          </div>
          {result.unmatched_samples?.length > 0 && (
            <div style={{ marginTop: "12px", fontFamily: "Manrope", fontSize: "11px", color: "#6b7280" }}>
              <div style={{ marginBottom: "4px" }}>Без совпадения (примеры): </div>
              <div style={{ fontFamily: "monospace", background: "white", padding: "8px", borderRadius: "4px", border: "1px solid #f3f4f6", fontSize: "10px", maxHeight: "100px", overflow: "auto" }}>
                {result.unmatched_samples.join(", ")}
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={upload} disabled={!zipFile || uploading} data-testid="bulk-zip-upload-btn"
        style={{ marginTop: "16px", background: zipFile && !uploading ? "linear-gradient(135deg,#C5A059,#D4B878)" : "#e5e7eb", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "12px 18px", cursor: zipFile && !uploading ? "pointer" : "default", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
        {uploading ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Upload size={14} />}
        {uploading ? "Загружается..." : "Привязать фотографии"}
      </button>
    </div>
  );
}

const th = { padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: "11px", color: "#6b7280", letterSpacing: "0.06em", textTransform: "uppercase" };
const td = { padding: "8px 12px", color: "#0A0A0A" };
